import {
  CART_COUNT_UPDATED_EVENT_NAME,
  LOCATION_UPDATED_EVENT_NAME,
  UPDATED_SHOP_DATA_EVENT,
  MERGED_CARTS_EVENT,
} from '@nike/custom-event-names';
import { CART_STATE_KEY } from '@nike/cart-constants';
import { SHOP_STATE_KEY } from '../../state/redux/action-types';
import determinePostalCode from '../../business-logic/determine-postal-code';
import determineAddressVsCountry from '../../business-logic/determine-address-vs-country';
import getWebShellClientLocale from '../../state/redux-replacement/get-webshellclient-locale';

/**
 * @memberof NikeShop
 * @name NikeShop.get
 * @returns {NikeShopAccessors} - properties available on object returned by NikeShop.get for consumers of nike-shop-client to use
 *
 * @example <caption>Using accessors for supported values</caption>
 * const cartCount = NikeShop.get().totalCartQuantity;
 * const cartHref = NikeShop.get().cartHref;
 *
 * @example <caption>Using event listeners and preferred pickup location</caption>
 * window.addEventListener(NikeShop.get().events.locationUpdated, ({ detail }) => {
 *   const { postalCode, locationId } = detail;
 *   if(locationId) {
 *     const storeName = NikeShop.get().preferredPickupLocation.name
 *     ...
 *    }
 * });
 */
export default function get(privateVars) {
  const { queryClient } = privateVars;
  const country = getWebShellClientLocale().country?.toUpperCase();
  const locationId = this.internalStore.getState()?.[SHOP_STATE_KEY]?.location?.locationId || '';

  return {
    country,
    postalCode: determinePostalCode({
      userLocationPostalCode:
        this.internalStore?.getState()?.[SHOP_STATE_KEY]?.location?.postalCode,
      profileAddressPostalCode: queryClient?.getQueryData(['profileAddress'])?.postalCode,
    }),
    locationId,
    locations:
      this.internalStore.getState()?.[SHOP_STATE_KEY]?.normalized?.locationSearchResults || {},
    preferredAddress: determineAddressVsCountry(
      queryClient?.getQueryData(['profileAddress']),
      country,
    ),
    preferredPickupLocation:
      this.internalStore.getState()?.[SHOP_STATE_KEY]?.normalized?.locationSearchResults?.[
        locationId
      ]?.details || {},
    promoCodes: this.internalStore.getState()?.[SHOP_STATE_KEY]?.normalized?.promoCodes || {},
    cartItems: this.internalStore.getState()?.[SHOP_STATE_KEY]?.normalized?.cartItems || {},
    cart: {
      id: this.internalStore.getState()?.[SHOP_STATE_KEY]?.normalized?.cart?.[country]?.id,
      items: this.store.getState()?.[CART_STATE_KEY]?.cart?.items || [],
      currency:
        this.internalStore.getState()?.[SHOP_STATE_KEY]?.normalized?.cart?.[country]?.currency,
    },
    list: {
      listItems: this.internalStore.getState()?.[SHOP_STATE_KEY]?.normalized?.listItems,
    },
    vas: this.internalStore.getState()?.[SHOP_STATE_KEY]?.normalized?.vas || {},
    cartTotals: this.internalStore.getState()?.[SHOP_STATE_KEY]?.normalized?.cartTotals?.[country],
    errors: this.internalStore.getState()?.[SHOP_STATE_KEY]?.errors || [],
    warnings: this.internalStore.getState()?.[SHOP_STATE_KEY]?.warnings || [],
    offeringItems: this.internalStore.getState()?.[SHOP_STATE_KEY]?.normalized?.offeringItems || {},
    fulfillmentTypes: this.internalStore
      .getState()
      ?.[
        SHOP_STATE_KEY
      ]?.normalized?.availableFulfillmentTypes?.[country]?.fulfillmentTypes?.filter(type => type !== 'INSTORE'),
    availableVas: this.internalStore.getState()?.[SHOP_STATE_KEY]?.normalized?.availableVas || {},
    events: {
      locationUpdated: LOCATION_UPDATED_EVENT_NAME,
      sharedDataUpdated: UPDATED_SHOP_DATA_EVENT,
      mergedCarts: MERGED_CARTS_EVENT,
      cartCountUpdated: CART_COUNT_UPDATED_EVENT_NAME,
      listsInitialized: '@nikeShopEvents/listsInitialized',
    },
    cartHref: this.internalStore.getState()?.[SHOP_STATE_KEY]?.shared?.cartHref,
    favoritesHref: this.internalStore.getState()?.[SHOP_STATE_KEY]?.shared?.favoritesHref,
    checkoutHref: this.internalStore.getState()?.[SHOP_STATE_KEY]?.shared?.checkoutHref,
    totalCartQuantity:
      this.internalStore.getState()?.[SHOP_STATE_KEY]?.normalized?.cart?.[country]?.totals
        ?.quantity,
  };
}
